
import { getCookies } from 'cookies-next';
import getT from 'next-translate/getT';
import loadNamespaces from 'next-translate/loadNamespaces';
import { UAParser } from 'ua-parser-js';

import env from '@/core/env';
import { routesPreset } from '@/core/routes';

import type { PageProps } from '@/types/pages';
import type { GetServerSidePropsContext } from 'next';
export type { GetServerSidePropsContext };

interface Params {
  redirectIfLogged?: boolean;
  redirect?: {
    allow?: boolean,
    permanent?: boolean,
    destination: '/404' | string,
  };
  seoConfig?: {
    namespace?: string;
    title: string;
    description?: string;
  };
  headers?: Record<string, string>;
  cacheDurationInMinutes?: number;
}

const isAuthenticated = async (context: GetServerSidePropsContext): Promise<boolean> => {
  const cookies = await getCookies(context) || {};
  const accessToken = cookies['access_token'];
  // Add more checks if needed (e.g., token expiration, validity)
  return !!accessToken;
};

export const getCustomServerSideProps = async (
  context: GetServerSidePropsContext,
  pageProps: PageProps,
  params: Params = {},
) => {

  // Remove 'ref' from context.query to avoid passing it as a prop if necessary
  delete context.query?.ref;

  // Set custom headers if provided
  const { res, resolvedUrl } = context;

  // get  domain from context
  const domain = context.req.headers.host || '';


  // Set cache control headers (only for production)
  if (env.isProd) {
    let defaultCacheTime = 60; // 1 minute
    if (typeof params.cacheDurationInMinutes === 'number') {
      defaultCacheTime = params.cacheDurationInMinutes * 60;
    }
    res.setHeader('Cache-Control', `s-maxage=${defaultCacheTime}, stale-while-revalidate=300`);
  }

  if (params.headers) {
    Object.entries(params.headers).forEach(([key, value]) => {
      res.setHeader(key, value);
    });
  }

  // Determine if redirection is required
  let redirect: {
    destination: string;
    permanent: boolean;
  } | undefined;

  if (params.redirectIfLogged && await isAuthenticated(context)) {
    redirect = {
      destination: routesPreset.userLogged,
      permanent: false,
    };
  } else if (params.redirect && params.redirect.allow !== false) {
    redirect = {
      destination: params.redirect.destination,
      permanent: params.redirect.permanent || false,
    };
  }

  // Get device type from user agent
  const userAgent = context.req.headers['user-agent'] || '';
  const getDeviceType = () => {
    try {
      const parser = new UAParser(userAgent);
      const deviceType = parser.getDevice().type || 'desktop';
      return deviceType;
    } catch (error) {
      console.error('Error parsing user agent:', error);
      return 'desktop';
    }
  };

  // Load namespaces for translations
  const locale = context.locale;
  const pathname = context.resolvedUrl;
  const namespaces = await loadNamespaces({ locale, pathname });

  // Handle SEO configuration if provided
  let seo = pageProps.config?.seo || {};
  if (params.seoConfig) {
    const t = params.seoConfig.namespace ? await getT(locale, params.seoConfig.namespace) : (value: string) => value;
    const { title, description } = params.seoConfig;
    seo = {
      ...seo,
      title: t(title),
      description: description ? t(description) : '',
    };
  }

  const response: {
    props: PageProps;
    redirect?: {
      destination: string;
      permanent: boolean;
    };
  } = {
    props: {
      ...pageProps,
      ...namespaces,

      config: {
        ...pageProps.config,
        seo,
      },
      server: {
        deviceType: getDeviceType(),
        resolvedUrl,
        domain,
      },
    },
    redirect,
  };

  return response;
};
